import { Block, BlockContent, DecompositionMode, isPrestation } from '../types';
import { BlockCalculationService } from './BlockCalculationService';

// Sous-ensemble de la feuille de vente suffisant pour le calcul MO.
// On évite une dépendance directe sur FeuilleDeVenteState pour limiter le couplage.
export interface DecompositionParams {
  tauxHoraire: number;        // €/h
  tempsApproDefault: number;  // h/ligne (forfait par défaut)
  /** Coef multiplicateur par défaut appliqué au PU de la ligne (override possible par ligne). */
  coefDefaut?: number;
  /** Frais de chantier par défaut (%, niveau affaire — pas appliqué ligne à ligne dans ce service). */
  fraisChantierDefaut?: number;
  /** Frais généraux par défaut (%, niveau affaire). */
  fraisGenerauxDefaut?: number;
  /** Marge commerciale cible par défaut (%, niveau affaire). */
  margeDefaut?: number;
}

export interface DecompositionBreakdown {
  coutFourniture: number;    // €/unité
  coutMOUnitaire: number;    // €/unité
  coutApproLigne: number;    // € (forfait ligne, amorti sur qté dans le PU)
  coutApproParUnite: number; // €/unité (coutApproLigne / qté)
  /** PU coût calculé depuis la décomposition (F + MO + Appro/qté). Propagé tel quel à la ligne. */
  prixUnitaireCout: number;
  /** PU propagé à la ligne (= prixUnitaireCout — plus d'override vente). */
  prixUnitaire: number;
  /** Coef ligne effectivement appliqué (override ligne si saisi, sinon params.coefDefaut, sinon 1). */
  coefApplique: number;
  /** Total ligne = PU × coef × qté (appro déjà inclus dans PU). */
  prixTotal: number;
}

export class DecompositionCalculationService {
  /** Résout le mode avec valeur par défaut 'forfaitaire' (rétrocompat). */
  static getMode(block: Block): DecompositionMode {
    return block.content.decompositionMode ?? 'forfaitaire';
  }

  static isDetaillee(block: Block): boolean {
    const m = this.getMode(block);
    return m === 'fourniture-seule' || m === 'fourni-pose';
  }

  /** Accepte number | string | undefined (les inputs peuvent rester typés string). */
  static parseNumericInput(value: number | string | undefined): number {
    if (typeof value === 'number') return isFinite(value) ? value : 0;
    if (!value) return 0;
    return BlockCalculationService.parsePrice(value);
  }

  /**
   * Résout le coef ligne effectif : override ligne > coefDefaut params > 1.
   * Un coef ≤ 0 n'a pas de sens métier et retombe sur le défaut.
   */
  static resolveCoef(content: BlockContent, params: Pick<DecompositionParams, 'coefDefaut'>): number {
    const override = content.coefLigne;
    if (typeof override === 'number' && isFinite(override) && override > 0) return override;
    const def = params.coefDefaut;
    if (typeof def === 'number' && isFinite(def) && def > 0) return def;
    return 1;
  }

  /** Calcul détaillé de la ligne selon son mode de décomposition. */
  static computeBreakdown(
    content: BlockContent,
    quantite: number,
    params: DecompositionParams,
  ): DecompositionBreakdown {
    const mode: DecompositionMode = content.decompositionMode ?? 'forfaitaire';
    const coutFourniture = this.parseNumericInput(content.coutFourniture);
    const coefApplique = this.resolveCoef(content, params);

    if (mode === 'fourniture-seule') {
      const prixUnitaireCout = coutFourniture;
      return {
        coutFourniture,
        coutMOUnitaire: 0,
        coutApproLigne: 0,
        coutApproParUnite: 0,
        prixUnitaireCout,
        prixUnitaire: prixUnitaireCout,
        coefApplique,
        prixTotal: prixUnitaireCout * coefApplique * quantite,
      };
    }

    if (mode === 'fourni-pose') {
      const tempsPose = this.parseNumericInput(content.tempsPose);
      const nbHommes = this.parseNumericInput(content.nbHommes);
      const tempsAppro = content.tempsAppro !== undefined
        ? this.parseNumericInput(content.tempsAppro)
        : params.tempsApproDefault;
      const coutMOUnitaire = tempsPose * nbHommes * params.tauxHoraire;
      const coutApproLigne = tempsAppro * params.tauxHoraire;
      // Appro : forfait ligne amorti sur la quantité pour composer le PU.
      // On intègre Appro/qté dans le prix unitaire pour que le PU reflète
      // l'intégralité du coût ligne (et pour que le coef/marge s'applique
      // aussi à l'appro). Edge case qté=0 → 0 (la ligne vaut 0 de toute façon).
      const coutApproParUnite = quantite > 0 ? coutApproLigne / quantite : 0;
      const prixUnitaireCout = coutFourniture + coutMOUnitaire + coutApproParUnite;
      return {
        coutFourniture,
        coutMOUnitaire,
        coutApproLigne,
        coutApproParUnite,
        prixUnitaireCout,
        prixUnitaire: prixUnitaireCout,
        coefApplique,
        prixTotal: prixUnitaireCout * coefApplique * quantite,
      };
    }

    // forfaitaire : pas de décomposition, les champs existants gouvernent.
    // Le total persisté dans content prime (recalculé par BlockRow.handleFieldSave
    // quand quantité/PU/coef changent, ou saisi manuellement par l'utilisateur).
    const puForfaitaire = BlockCalculationService.parsePrice(content.prixUnitaire ?? '0');
    return {
      coutFourniture: 0,
      coutMOUnitaire: 0,
      coutApproLigne: 0,
      coutApproParUnite: 0,
      prixUnitaireCout: puForfaitaire,
      prixUnitaire: puForfaitaire,
      coefApplique,
      prixTotal: BlockCalculationService.parsePrice(content.prixTotal ?? '0'),
    };
  }

  /**
   * Applique la décomposition au block et met à jour prixUnitaire / prixTotal
   * dans le content (persistance). Retourne le content mis à jour — laisse
   * l'appelant fusionner dans le block.
   *
   * Met aussi prixTotalManual à false car le calcul prime en mode détaillé.
   */
  static applyBreakdownToContent(
    content: BlockContent,
    quantiteRaw: string | undefined,
    params: DecompositionParams,
  ): BlockContent {
    if (!this.isDetailleeContent(content)) return content;
    const qty = BlockCalculationService.parseQuantity(quantiteRaw ?? '1');
    const br = this.computeBreakdown(content, qty, params);
    return {
      ...content,
      prixUnitaire: BlockCalculationService.formatPrice(br.prixUnitaire),
      prixTotal: BlockCalculationService.formatPrice(br.prixTotal),
      prixTotalManual: false,
    };
  }

  /** Même règle que isDetaillee mais au niveau du content (utilitaire interne). */
  static isDetailleeContent(content: BlockContent): boolean {
    const m = content.decompositionMode ?? 'forfaitaire';
    return m === 'fourniture-seule' || m === 'fourni-pose';
  }

  /**
   * Recalcule tous les blocs d'une liste suite à un changement des paramètres
   * globaux (tauxHoraire, coefDefaut, ...).
   *
   * - Détaillé (fourniture-seule / fourni-pose) → passe par applyBreakdownToContent
   *   (intègre PU vente override, coef, appro).
   * - Forfaitaire non-manuel → total = PU × coef × qty (respecte prixTotalManual).
   * - Forfaitaire manuel → laissé intact.
   */
  static recomputeBlocks(blocks: Block[], params: DecompositionParams): Block[] {
    return blocks.map(b => {
      if (!isPrestation(b)) return b;
      if (this.isDetaillee(b)) {
        const content = this.applyBreakdownToContent(b.content, b.content.quantite, params);
        return { ...b, content };
      }
      if (b.content.prixTotalManual) return b;
      const qty = BlockCalculationService.parseQuantity(b.content.quantite ?? '1');
      const pu = BlockCalculationService.parsePrice(b.content.prixUnitaire ?? '0');
      const coef = this.resolveCoef(b.content, params);
      const nextTotal = BlockCalculationService.formatPrice(qty * pu * coef);
      if (nextTotal === b.content.prixTotal) return b;
      return { ...b, content: { ...b.content, prixTotal: nextTotal } };
    });
  }
}
